import { useEffect } from 'react';
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useToastStore, type ToastEntry, type ToastType } from '../../../store/toastStore';
import styles from './Toast.module.css';

const ICON_BY_TYPE: Record<ToastType, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: AlertCircle,
  warning: AlertTriangle,
  info: Info,
};

interface ToastItemProps {
  toast: ToastEntry;
  onDismiss: (id: string) => void;
}

function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const Icon = ICON_BY_TYPE[toast.type];

  useEffect(() => {
    const ms = toast.duration ?? 4000;
    if (ms <= 0) return;
    const id = window.setTimeout(() => onDismiss(toast.id), ms);
    return () => window.clearTimeout(id);
  }, [toast.duration, toast.id, onDismiss]);

  return (
    <div role="status" aria-live="polite" className={cn(styles.toast, styles[toast.type])}>
      <Icon size={18} aria-hidden className={styles.icon} />
      <span className={styles.body}>{toast.message}</span>
      <button
        type="button"
        onClick={() => onDismiss(toast.id)}
        className={styles.close}
        aria-label="Dismiss"
      >
        <X size={14} aria-hidden />
      </button>
    </div>
  );
}

export function ToastContainer() {
  const toasts = useToastStore((s) => s.toasts);
  const dismiss = useToastStore((s) => s.dismiss);

  if (toasts.length === 0) return null;
  return (
    <div className={styles.container} aria-live="polite" aria-label="Notifications">
      {toasts.map((t) => (
        <ToastItem key={t.id} toast={t} onDismiss={dismiss} />
      ))}
    </div>
  );
}
